import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, Mail, Phone, MapPin, Rocket, Target, Users, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../sections/Navbar';
import Footer from '../sections/Footer';

gsap.registerPlugin(ScrollTrigger);

const AboutPage = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const ctx = gsap.context(() => {
      gsap.from(contentRef.current?.querySelectorAll('.animate-section') || [], {
        scrollTrigger: {
          trigger: contentRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
        opacity: 0,
        y: 30,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
      <Navbar />
      
      <main className="relative z-10 pt-24 pb-16">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/">
              <Button
                variant="ghost"
                className="mb-6 text-slate-500 hover:text-slate-700 -ml-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zpět na hlavní stránku
              </Button>
            </Link>

            <div ref={contentRef}>
              {/* Header */}
              <div className="animate-section text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-4">
                  <Sparkles className="w-4 h-4 text-[#0ea5e9]" />
                  <span className="text-sm text-slate-600">O nás</span>
                </div>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-slate-800">
                  Jsme <span className="text-gradient">mladá firma</span> s velkým cílem
                </h1>
                
                <p className="text-lg text-slate-500 max-w-2xl mx-auto">
                  Vidíme obrovský potenciál v AI vyhledávání a chceme pomáhat firmám 
                  být vidět v nové éře digitálního marketingu.
                </p>
              </div>

              {/* Our Story */}
              <div className="animate-section glass-card rounded-2xl p-6 md:p-8 mb-8">
                <h2 className="text-2xl font-bold mb-4 text-slate-800">Náš příběh</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  AI Optimalizace vznikla z jednoduchého pozorování: svět vyhledávání se mění. 
                  ChatGPT, Perplexity a další AI nástroje přepisují pravidla hry. 
                  Firmy, které se přizpůsobí, získají obrovskou výhodu.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Jsme mladí zakladatelé, kteří vidí budoucnost v AI. Chceme růst 
                  a pomáhat dalším firmám růst s námi. Věříme, že správná optimalizace 
                  pro AI vyhledávače může změnit byznys.
                </p>
              </div>

              {/* Values */}
              <div className="animate-section grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="glass-card rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0ea5e9]/20 to-[#00d4aa]/20 flex items-center justify-center mx-auto mb-3">
                    <Rocket className="w-6 h-6 text-[#0ea5e9]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">Inovace</h3>
                  <p className="text-sm text-slate-500">
                    Jsme na špičce vývoje. Sledujeme každou změnu v AI světě.
                  </p>
                </div>
                
                <div className="glass-card rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00d4aa]/20 to-[#14b8a6]/20 flex items-center justify-center mx-auto mb-3">
                    <Target className="w-6 h-6 text-[#00d4aa]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">Výsledky</h3>
                  <p className="text-sm text-slate-500">
                    Měřitelné výsledky. Žádné plané sliby, jen reálná čísla.
                  </p>
                </div>
                
                <div className="glass-card rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#14b8a6]/20 to-[#22c55e]/20 flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6 text-[#14b8a6]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">Partnerství</h3>
                  <p className="text-sm text-slate-500">
                    Rosteme společně s našimi klienty. Vaše úspěch je náš úspěch.
                  </p>
                </div>
              </div>

              {/* Contact */}
              <div className="animate-section glass-card rounded-2xl p-6 md:p-8">
                <h2 className="text-2xl font-bold mb-6 text-slate-800 flex items-center gap-2">
                  <Mail className="w-6 h-6 text-[#0ea5e9]" />
                  Kontakt
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0ea5e9]/20 to-[#00d4aa]/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-[#0ea5e9]" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-500">Kontaktní osoba</div>
                        <div className="font-semibold text-slate-800">Šimon Souček</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00d4aa]/20 to-[#14b8a6]/20 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-[#00d4aa]" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-500">Telefon</div>
                        <a href="tel:+420737509653" className="font-semibold text-slate-800 hover:text-[#0ea5e9] transition-colors">
                          +420 737 509 653
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#14b8a6]/20 to-[#22c55e]/20 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-[#14b8a6]" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-500">E-mail</div>
                        <a href="mailto:simonsoucek99@gmail.com" className="font-semibold text-slate-800 hover:text-[#0ea5e9] transition-colors">
                          simonsoucek99@gmail.com
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#22c55e]/20 to-[#0ea5e9]/20 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#22c55e]" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">Sídlo</div>
                      <div className="font-semibold text-slate-800">Praha, Česká republika</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AboutPage;
